<template>
  <div class="relative" :class="colSpan">
    <label class="text-xs" :for="field.handle">
      {{ label || field.label }}
    </label>

    <div class="flex">
      <!-- PHONES -->

      <Listbox v-model="selectedPhone" as="div" class="shrink-0">
        <ListboxButton
          class="focus-default h-[3.75rem] w-full space-x-3 rounded-l-md bg-second-blue-200/50 px-5 text-base ring-offset-0 ui-open:rounded-b-none"
        >
          <transition name="fade" mode="out-in" duration="100">
            <img
              :key="selectedPhone.flag?.permalink"
              class="w-6"
              :src="selectedPhone.flag?.permalink"
              :class="{
                'opacity-50': !selectedPhone.iso2,
              }"
            />
          </transition>
        </ListboxButton>

        <transition name="fade">
          <ListboxOptions
            class="focus-default absolute z-20 max-h-56 w-full overflow-auto rounded-b-md rounded-t-none border-x border-b bg-white ring-offset-0"
          >
            <ListboxOption
              v-for="country in countries"
              :key="country.iso2"
              :value="country"
              class="flex cursor-pointer items-center space-x-3 px-5 py-2 hover:bg-second-blue-200/50 ui-selected:bg-main-blue/50 ui-selected:text-white ui-active:bg-second-blue-200/50 ui-selected:ui-active:bg-main-blue-hover/50"
            >
              <img class="w-6 shrink-0" :src="country.flag?.permalink" />
              <p>{{ country.title }} (+{{ country.phone_code }})</p>
            </ListboxOption>
          </ListboxOptions>
        </transition>
      </Listbox>

      <!-- INPUT FIELD -->
      <Field
        ref="inputField"
        v-model="innerValue"
        class="focus-default block h-[3.75rem] w-full rounded-r-md bg-second-blue-200 bg-opacity-50 text-base"
        :class="{
          'border-main-red': error,
        }"
        :name="field.handle"
        :label="field.label || field.display"
        :type="field.config.input_type"
        :placeholder="field.config?.placeholder || label"
        :disabled="disabled"
        :rules="rulesAsString"
      />
    </div>

    <transition name="fade">
      <p v-if="error" class="mt-1 text-xs text-red-500">{{ error }}</p>
    </transition>
  </div>
</template>

<script setup>
import { Field } from 'vee-validate'
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from '@headlessui/vue'

const props = defineProps({
  countries: { type: Array, required: true },
  field: { type: Object, required: true },
  value: { type: null, default: '' },
  rules: { type: [Array, String], default: '' },
  error: { type: String, default: null },
  disabled: { type: Boolean, default: false },
  label: { type: String, default: '' },
})

const emit = defineEmits(['input'])
const innerValue = ref(props.value)
const selectedPhone = ref({
  flag: {
    permalink: '/images/globe-icon.svg',
  },
})
const inputField = ref(null)

watch(innerValue, (currentValue) => {
  emit('input', currentValue)
  matchCountry(currentValue)
})

watch(selectedPhone, (currentValue) => {
  innerValue.value = `+${currentValue.phone_code}`
  focusInput()
})

const rulesAsString = computed(() => {
  if (Array.isArray(props.rules)) {
    return `${props.rules.join('|')}|phone`
  }

  return 'phone'
})

const colSpan = computed(() => {
  const width = props.field.width || 100

  switch (width) {
    case 25:
      return 'sm:col-span-3 col-span-2'
    case 33:
      return 'sm:col-span-4 col-span-4'
    case 50:
      return 'sm:col-span-6 col-span-4'
    case 66:
      return 'sm:col-span-8 col-span-4'
    default:
      return 'sm:col-span-12 col-span-4'
  }
})

async function focusInput() {
  await nextTick()
  inputField.value.$el.focus()
}

function matchCountry(currentValue) {
  if (!currentValue) {
    return
  }

  let countries = props.countries

  let bestMatch = ''
  let bestMatchLength = 0
  const normalizedPhoneNumber = currentValue.replace(/\D/g, '')
  for (let i = 0; i < countries.length; i++) {
    const normalizedPhoneCode = countries[i].phone_code.replace(/\D/g, '')
    if (
      normalizedPhoneNumber.startsWith(normalizedPhoneCode) &&
      normalizedPhoneCode.length > bestMatchLength
    ) {
      bestMatch = countries[i]
      bestMatchLength = normalizedPhoneCode.length
    }
  }

  if (!bestMatch || bestMatch === selectedPhone.value) {
    return
  }

  selectedPhone.value = bestMatch
}
</script>
